/**
 * ConfirmDialog + ConfirmDialogProvider + useConfirm().
 *
 * Barrierefreier Ersatz für window.confirm(): role="dialog" + aria-modal,
 * Fokus-Trap (useFocusTrap), ESC = Abbrechen, Initial-Fokus auf dem
 * Abbrechen-Button (sichere Default bei destruktiven Aktionen), Body-Scroll-
 * Lock. Promise-basierte API, damit die Callsites 1:1 migrieren können:
 *
 * ```tsx
 * const confirm = useConfirm();
 * const proceed = await confirm({ title: de.…, tone: 'danger' });
 * if (!proceed) return;
 * ```
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useFocusTrap } from '../a11y/useFocusTrap';
import { de } from '../../lib/i18n/de';
import { Button } from './Button';

export interface ConfirmOptions {
  readonly title: string;
  readonly body?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: 'default' | 'danger';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm muss innerhalb von <ConfirmDialogProvider> verwendet werden.');
  }
  return ctx;
}

interface PendingConfirm {
  readonly options: ConfirmOptions;
  readonly resolve: (result: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { readonly children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending((prev) => {
          // Falls bereits ein Dialog offen ist: den alten als abgebrochen auflösen
          prev?.resolve(false);
          return { options, resolve };
        });
      }),
    [],
  );

  const close = useCallback((result: boolean) => {
    setPending((prev) => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog options={pending.options} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

interface ConfirmDialogProps {
  readonly options: ConfirmOptions;
  readonly onClose: (result: boolean) => void;
}

function ConfirmDialog({ options, onClose }: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Initial-Fokus landet auf dem ersten fokussierbaren Element — das ist per
  // DOM-Reihenfolge der Abbrechen-Button (sichere Default bei Destruktivem).
  useFocusTrap(dialogRef);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Body-Scroll-Lock, solange der Dialog offen ist
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const titleId = 'confirm-dialog-title';
  const bodyId = 'confirm-dialog-body';
  const isDanger = options.tone === 'danger';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-5"
      onClick={() => {
        onClose(false);
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.body ? bodyId : undefined}
        className="w-full max-w-md rounded-sm border border-stone-200 bg-white p-6 shadow-lg md:p-8"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2
          id={titleId}
          className="font-serif text-2xl font-bold leading-snug tracking-tight text-stone-900"
        >
          {options.title}
        </h2>
        {options.body && (
          <p id={bodyId} className="mt-3 whitespace-pre-line font-serif text-sm text-stone-700">
            {options.body}
          </p>
        )}
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              onClose(false);
            }}
          >
            {options.cancelLabel ?? de.confirmDialog.defaultCancelLabel}
          </Button>
          <Button
            type="button"
            variant={isDanger ? 'danger' : 'primary'}
            onClick={() => {
              onClose(true);
            }}
          >
            {options.confirmLabel ?? de.confirmDialog.defaultConfirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
